import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Button, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Stepper, Step, StepLabel, StepContent,
  Avatar, TextField, InputAdornment, IconButton, Tabs, Tab
} from '@mui/material';
import {
  Visibility, Inventory2, CheckCircle, AccountBalance, CreditCard,
  QrCode2, PaymentsOutlined, Search, Refresh, TrendingUp
} from '@mui/icons-material';

const ORDER_FLOW = [
  { status: 'pending',               label: 'Order Received',         icon: '📋', supplierAction: false },
  { status: 'confirmed',             label: 'Order Confirmed',         icon: '✅', supplierAction: true  },
  { status: 'processing',            label: 'Processing',              icon: '⚙️', supplierAction: true  },
  { status: 'manufacturing',         label: 'Manufacturing',           icon: '🏭', supplierAction: true  },
  { status: 'quality_check',         label: 'Quality Check',           icon: '🔍', supplierAction: true  },
  { status: 'ready_to_ship',         label: 'Ready to Ship',           icon: '📦', supplierAction: true  },
  { status: 'in_transit_domestic',   label: 'In Transit (Domestic)',   icon: '🚛', supplierAction: false },
  { status: 'at_warehouse',          label: 'At OSESG Warehouse',      icon: '🏢', supplierAction: false },
  { status: 'shipped_international', label: 'Shipped International',   icon: '✈️', supplierAction: false },
  { status: 'customs_clearance',     label: 'Customs Clearance',       icon: '🛃', supplierAction: false },
  { status: 'delivered',             label: 'Delivered',               icon: '🎉', supplierAction: false },
  { status: 'completed',             label: 'Completed',               icon: '🏆', supplierAction: false },
];

const SUPPLIER_ALLOWED = ['confirmed', 'processing', 'manufacturing', 'quality_check', 'ready_to_ship'];

const statusColors = {
  pending: 'warning', confirmed: 'info', processing: 'primary', manufacturing: 'secondary',
  quality_check: 'default', ready_to_ship: 'success', in_transit_domestic: 'primary',
  at_warehouse: 'default', shipped_international: 'secondary', customs_clearance: 'warning',
  delivered: 'success', completed: 'success', cancelled: 'error', disputed: 'error'
};

const paymentIcons = {
  bank_transfer: <AccountBalance sx={{ fontSize: 16 }} />,
  bank_card:     <CreditCard sx={{ fontSize: 16 }} />,
  paypal:        <PaymentsOutlined sx={{ fontSize: 16 }} />,
  alipay:        <QrCode2 sx={{ fontSize: 16 }} />,
  wechat_pay:    <QrCode2 sx={{ fontSize: 16 }} />
};

const SupplierOrders = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [search, setSearch] = useState('');
  const [tabVal, setTabVal] = useState(0);

  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/orders');
      setOrders(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdating(true);
    try {
      await api.put(`/orders/${orderId}/status`, { status: newStatus, note: statusNote || `Status updated to ${newStatus}` });
      setStatusNote('');
      await loadOrders();
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        setSelectedOrder(data.data);
      } catch {}
    } catch (err) { console.error(err); }
    setUpdating(false);
  };

  const openDetail = async (order) => {
    setSelectedOrder(order);
    setDetailDialog(true);
    try {
      const { data } = await api.get(`/orders/${order._id}`);
      setSelectedOrder(data.data);
    } catch {}
  };

  const currentStepIndex = (order) => ORDER_FLOW.findIndex(s => s.status === order?.status);

  const filteredOrders = orders.filter(o => {
    const matchSearch = !search || o.orderNumber?.toLowerCase().includes(search.toLowerCase());
    if (tabVal === 1) return matchSearch && SUPPLIER_ALLOWED.includes(o.status);
    if (tabVal === 2) return matchSearch && ['delivered', 'completed'].includes(o.status);
    return matchSearch;
  });

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t('incoming_orders')}</Typography>
          <Typography variant="body2" color="text.secondary">
            Customer identities are masked for privacy — you see masked IDs only.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <TextField size="small" placeholder="Search order #..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          <IconButton onClick={loadOrders}><Refresh /></IconButton>
        </Box>
      </Box>

      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        {[
          { label: 'Total', count: orders.length, color: 'default' },
          { label: 'Action Required', count: orders.filter(o => SUPPLIER_ALLOWED.includes(o.status)).length, color: 'warning' },
          { label: 'Completed', count: orders.filter(o => ['delivered', 'completed'].includes(o.status)).length, color: 'success' },
        ].map(s => (
          <Chip key={s.label} icon={<TrendingUp sx={{ fontSize: 16 }} />}
            label={`${s.label}: ${s.count}`} color={s.color} variant="outlined" />
        ))}
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabVal} onChange={(_, v) => setTabVal(v)}>
          <Tab label="All Orders" />
          <Tab label="Needs Action" />
          <Tab label="Completed" />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Order #</strong></TableCell>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>Items</strong></TableCell>
              <TableCell><strong>Payment</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.map((order) => (
              <TableRow key={order._id} hover
                sx={{ bgcolor: SUPPLIER_ALLOWED.includes(order.status) ? '#fffde7' : 'inherit' }}>
                <TableCell><strong>{order.orderNumber}</strong></TableCell>
                <TableCell>
                  <Chip label={order.customer?.maskedName || `CUST-MASKED`} size="small" variant="outlined" />
                  <Typography variant="caption" display="block" color="text.secondary">
                    {order.customer?.country}
                  </Typography>
                </TableCell>
                <TableCell>
                  {order.items?.map((item, i) => (
                    <Typography key={i} variant="caption" display="block">
                      {item.osesgCode || item.name} ×{item.quantity}
                    </Typography>
                  ))}
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {paymentIcons[order.paymentMethod] || <AccountBalance sx={{ fontSize: 16 }} />}
                    <Typography variant="caption">
                      {(order.paymentMethod || 'N/A').replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                  </Box>
                  <Chip label={order.paymentStatus || 'unpaid'} size="small"
                    color={order.paymentStatus === 'paid' ? 'success' : 'warning'} sx={{ mt: 0.5 }} />
                </TableCell>
                <TableCell>
                  <Chip label={order.status?.replace(/_/g, ' ')} size="small"
                    color={statusColors[order.status] || 'default'} />
                  {SUPPLIER_ALLOWED.includes(order.status) && (
                    <Typography variant="caption" display="block" color="warning.main" fontWeight={600}>
                      ⚡ Action needed
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Button size="small" variant="contained" startIcon={<Visibility />}
                    onClick={() => openDetail(order)}>
                    Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>No orders found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Order Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory2 /> Order: {selectedOrder?.orderNumber}
          <Chip label={selectedOrder?.status?.replace(/_/g, ' ')} size="small"
            sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedOrder && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Order Items</Typography>
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  {selectedOrder.items?.map((item, i) => (
                    <Box key={i} sx={{ px: 2, py: 1.5, borderBottom: i < selectedOrder.items.length - 1 ? '1px solid #eee' : 'none' }}>
                      <Typography variant="body2" fontWeight={600}>{item.name || item.osesgCode}</Typography>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          {item.osesgCode} · Qty: {item.quantity}
                        </Typography>
                        <Typography variant="caption" fontWeight={600}>
                          ${item.totalPrice?.toLocaleString()}
                        </Typography>
                      </Box>
                      {item.specifications && (
                        <Typography variant="caption" color="primary.main">Note: {item.specifications}</Typography>
                      )}
                    </Box>
                  ))}
                </Paper>

                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 2 }}>Customer (Masked)</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>ID:</strong> {selectedOrder.customer?.maskedName || 'MASKED'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Country:</strong> {selectedOrder.customer?.country || 'N/A'}
                  </Typography>
                </Paper>

                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 2 }}>Payment Method</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {paymentIcons[selectedOrder.paymentMethod]}
                    <Typography variant="body2" textTransform="capitalize">
                      {selectedOrder.paymentMethod?.replace(/_/g, ' ')}
                    </Typography>
                    <Chip label={selectedOrder.paymentStatus} size="small"
                      color={selectedOrder.paymentStatus === 'paid' ? 'success' : 'warning'} />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Order Process Timeline</Typography>
                <Box sx={{ maxHeight: 340, overflowY: 'auto', pr: 1 }}>
                  <Stepper orientation="vertical" activeStep={currentStepIndex(selectedOrder)}>
                    {ORDER_FLOW.map((step, index) => {
                      const current = currentStepIndex(selectedOrder);
                      const done = index < current;
                      const active = index === current;
                      return (
                        <Step key={step.status} completed={done}>
                          <StepLabel
                            StepIconComponent={() => (
                              <Avatar sx={{
                                width: 28, height: 28, fontSize: '0.75rem',
                                bgcolor: done ? 'success.main' : active ? 'primary.main' : '#e0e0e0',
                                color: done || active ? 'white' : 'text.disabled'
                              }}>
                                {done ? '✓' : step.icon}
                              </Avatar>
                            )}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" fontWeight={active || done ? 700 : 400}
                                color={active ? 'primary.main' : done ? 'success.main' : 'text.secondary'}>
                                {step.label}
                              </Typography>
                              {step.supplierAction && active && (
                                <Chip label="Your action" size="small" color="warning" sx={{ fontSize: '0.65rem' }} />
                              )}
                            </Box>
                          </StepLabel>
                          <StepContent>
                            {active && step.supplierAction && (
                              <Typography variant="caption" color="warning.main">
                                ⚡ Awaiting your update to proceed
                              </Typography>
                            )}
                          </StepContent>
                        </Step>
                      );
                    })}
                  </Stepper>
                </Box>

                {selectedOrder.statusHistory?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 2 }}>History</Typography>
                    <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>
                      {[...selectedOrder.statusHistory].reverse().map((h, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                          <CheckCircle sx={{ fontSize: 14, color: 'success.main', mt: 0.3, flexShrink: 0 }} />
                          <Box>
                            <Typography variant="caption" fontWeight={600} textTransform="capitalize">
                              {h.status?.replace(/_/g, ' ')}
                            </Typography>
                            {h.note && <Typography variant="caption" display="block" color="text.secondary">{h.note}</Typography>}
                            <Typography variant="caption" color="text.disabled">
                              {new Date(h.timestamp).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}

                {SUPPLIER_ALLOWED.includes(selectedOrder.status) && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom color="warning.dark">
                      ⚡ Update Order Status
                    </Typography>
                    <TextField fullWidth size="small" label="Note (optional)" value={statusNote}
                      onChange={(e) => setStatusNote(e.target.value)} sx={{ mb: 1.5 }} />
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {ORDER_FLOW
                        .filter(s => s.supplierAction)
                        .filter(s => {
                          const curr = currentStepIndex(selectedOrder);
                          const idx = ORDER_FLOW.findIndex(x => x.status === s.status);
                          return idx === curr + 1;
                        })
                        .map(s => (
                          <Button key={s.status} variant="contained" size="small"
                            disabled={updating}
                            onClick={() => handleStatusUpdate(selectedOrder._id, s.status)}>
                            {updating ? <CircularProgress size={14} /> : `→ ${s.label}`}
                          </Button>
                        ))
                      }
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierOrders;
